import { createContext, useContext, useState, useEffect, useCallback } from "react";
import translations from "../i18n";

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

const CURRENCY_RATES = { EUR: 1, USD: 1.08, GBP: 0.86 };
const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("tt_lang") || "fr");
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("tt_currency") || "EUR");
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tt_recent") || "[]"); } catch { return []; }
  });
  const [compareList, setCompareList] = useState([]);

  // Language
  const t = useCallback((key) => translations[lang]?.[key] || translations.fr[key] || key, [lang]);
  const switchLang = (newLang) => { setLang(newLang); localStorage.setItem("tt_lang", newLang); };

  // Currency
  const setCurrency = (c) => { setCurrencyState(c); localStorage.setItem("tt_currency", c); };
  const formatPrice = useCallback((priceInput) => {
    if (priceInput == null) return "";
    let eurValue;
    if (typeof priceInput === "string") {
      const cleaned = priceInput.replace(/[€$£\s]/g, "").replace(",", ".");
      eurValue = parseFloat(cleaned);
    } else {
      eurValue = parseFloat(priceInput);
    }
    if (isNaN(eurValue)) return priceInput;
    const converted = (eurValue * CURRENCY_RATES[currency]).toFixed(2);
    const formatted = converted.replace(".", ",");
    const sym = CURRENCY_SYMBOLS[currency];
    return currency === "EUR" ? `${formatted}${sym}` : `${sym}${formatted}`;
  }, [currency]);
  const currencySymbol = CURRENCY_SYMBOLS[currency];

  // Recently viewed
  const addRecentlyViewed = useCallback((product) => {
    setRecentlyViewed(prev => {
      const filtered = prev.filter(p => p.id !== product.id);
      const updated = [product, ...filtered].slice(0, 8);
      localStorage.setItem("tt_recent", JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Compare
  const toggleCompare = useCallback((product) => {
    setCompareList(prev => {
      if (prev.find(p => p.id === product.id)) return prev.filter(p => p.id !== product.id);
      if (prev.length >= 4) return prev;
      return [...prev, product];
    });
  }, []);
  const clearCompare = useCallback(() => setCompareList([]), []);
  const isInCompare = useCallback((id) => compareList.some(p => p.id === id), [compareList]);

  return (
    <AppContext.Provider value={{
      lang, t, switchLang,
      currency, setCurrency, formatPrice, currencySymbol,
      recentlyViewed, addRecentlyViewed,
      compareList, toggleCompare, clearCompare, isInCompare
    }}>
      {children}
    </AppContext.Provider>
  );
};
